// src/components/ReadjudicacionMasiva/Abogados/DesapoderamientoPanel.js

import React, { useState, useEffect } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
    Paper,
    Typography,
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Chip,
    CircularProgress,
    Snackbar,
    Alert
} from '@mui/material';
import {
    RemoveCircle as RemoveCircleIcon
} from '@mui/icons-material';

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(3)
    },
    title: {
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1)
    },
    filters: {
        marginBottom: theme.spacing(3)
    },
    tableContainer: {
        maxHeight: 500,
        marginBottom: theme.spacing(2)
    },
    selectedChip: {
        marginLeft: theme.spacing(1)
    },
    actionsBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: theme.spacing(2)
    },
    infoBox: {
        backgroundColor: theme.palette.info.light,
        color: theme.palette.info.contrastText,
        padding: theme.spacing(1, 2),
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(2)
    },
    statsBox: {
        display: 'flex',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    statChip: {
        fontWeight: 600
    }
}));

const DesapoderamientoPanel = ({
    registros = [],
    abogados = [],
    juzgados = [],
    caracteres = [],
    onDesapoderar,
    onFiltrar,
    loading
}) => {
    const classes = useStyles();

    const [abogadoFiltro, setAbogadoFiltro] = useState('');
    const [juzgadoFiltro, setJuzgadoFiltro] = useState('');
    const [caracterFiltro, setCaracterFiltro] = useState('');
    const [seleccionados, setSeleccionados] = useState([]);
    const [registrosFiltrados, setRegistrosFiltrados] = useState([]);
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'error' });
    const showSnack = (message, severity = 'error') => setSnack({ open: true, message, severity });

    // Cuando cambian los filtros, llamar al backend
    useEffect(() => {
        if (abogadoFiltro && onFiltrar) {
            // Llamar al backend para obtener registros filtrados
            onFiltrar({
                matricula: abogadoFiltro,
                idJuzgado: juzgadoFiltro || null,
                caracteres: caracterFiltro ? [caracterFiltro] : []
            }).then(data => {
                setRegistrosFiltrados(data || []);
                setSeleccionados([]);
            });
        } else {
            setRegistrosFiltrados([]);
            setSeleccionados([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [abogadoFiltro, juzgadoFiltro, caracterFiltro]);

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSeleccionados(registrosFiltrados.map(r => r.Id));
        } else {
            setSeleccionados([]);
        }
    };

    const handleSelectOne = (id) => {
        setSeleccionados(prev => {
            if (prev.includes(id)) {
                return prev.filter(item => item !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const handleDesapoderar = () => {
        if (seleccionados.length === 0) return;
        if (!abogadoFiltro) {
            showSnack('Debe seleccionar un abogado en el filtro', 'warning');
            return;
        }

        onDesapoderar(abogadoFiltro, seleccionados);
    };

    const isSelected = (id) => seleccionados.includes(id);

    // Obtener el nombre del abogado seleccionado
    const abogadoSeleccionado = abogados.find(a => a.Matricula === abogadoFiltro);

    return (
        <Paper className={classes.paper}>
            <Typography variant="h6" className={classes.title}>
                <RemoveCircleIcon color="secondary" />
                Desapoderar Abogados
            </Typography>

            <Typography variant="body2" color="text.secondary" gutterBottom>
                Seleccione el abogado a desapoderar y marque las causas donde se debe cambiar su carácter a "Reemplazado".
                Al finalizar el desapoderamiento, los abogados marcados serán removidos del lote.
            </Typography>

            {/* Estadísticas */}
            <Box className={classes.statsBox}>
                <Chip
                    label={`${registros.length} registros totales`}
                    variant="outlined"
                    className={classes.statChip}
                />
                <Chip
                    label={`${abogados.length} abogados en el lote`}
                    variant="outlined"
                    color="primary"
                    className={classes.statChip}
                />
                {abogadoFiltro && (
                    <Chip
                        label={`${registrosFiltrados.length} causas del abogado`}
                        variant="outlined"
                        color="secondary"
                        className={classes.statChip}
                    />
                )}
            </Box>

            {/* Filtros */}
            <Grid container spacing={2} className={classes.filters}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl variant="outlined" size="small" fullWidth>
                        <InputLabel>Abogado a Desapoderar *</InputLabel>
                        <Select
                            value={abogadoFiltro}
                            onChange={(e) => setAbogadoFiltro(e.target.value)}
                            label="Abogado a Desapoderar *"
                        >
                            <MenuItem value="">
                                <em>Seleccione un abogado</em>
                            </MenuItem>
                            {abogados.map((abogado) => (
                                <MenuItem key={abogado.Matricula} value={abogado.Matricula}>
                                    {abogado.Profesional} ({abogado.Matricula})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl variant="outlined" size="small" fullWidth>
                        <InputLabel shrink style={{ backgroundColor: 'white', padding: '0 4px' }}>Juzgado</InputLabel>
                        <Select
                            value={juzgadoFiltro}
                            onChange={(e) => setJuzgadoFiltro(e.target.value)}
                            label="Juzgado"
                            displayEmpty
                        >
                            <MenuItem value="">Todos los Juzgados</MenuItem>
                            {juzgados.map((juzgado) => (
                                <MenuItem key={juzgado.IdJuzgado || juzgado.JuzgadoRadicacion} value={juzgado.IdJuzgado}>
                                    {juzgado.JuzgadoRadicacion}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl variant="outlined" size="small" fullWidth>
                        <InputLabel shrink style={{ backgroundColor: 'white', padding: '0 4px' }}>Carácter</InputLabel>
                        <Select
                            value={caracterFiltro}
                            onChange={(e) => setCaracterFiltro(e.target.value)}
                            label="Carácter"
                            displayEmpty
                        >
                            <MenuItem value="">Todos los Caracteres</MenuItem>
                            {caracteres.map((caracter) => (
                                <MenuItem key={caracter.Id} value={caracter.Id}>
                                    {caracter.Nombre}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {/* Info del abogado seleccionado */}
            {abogadoSeleccionado && (
                <Box className={classes.infoBox}>
                    <Typography variant="body2">
                        <strong>Abogado seleccionado:</strong> {abogadoSeleccionado.Profesional} - Matrícula: {abogadoSeleccionado.Matricula}
                    </Typography>
                </Box>
            )}

            {/* Tabla */}
            <TableContainer className={classes.tableContainer}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    indeterminate={seleccionados.length > 0 && seleccionados.length < registrosFiltrados.length}
                                    checked={registrosFiltrados.length > 0 && seleccionados.length === registrosFiltrados.length}
                                    onChange={handleSelectAll}
                                    disabled={!abogadoFiltro}
                                />
                            </TableCell>
                            <TableCell style={{ fontWeight: 600 }}>Fila</TableCell>
                            <TableCell style={{ fontWeight: 600 }}>Autos</TableCell>
                            <TableCell style={{ fontWeight: 600 }}>Demandado</TableCell>
                            <TableCell style={{ fontWeight: 600 }}>Certificado</TableCell>
                            <TableCell style={{ fontWeight: 600, textAlign: 'right' }}>Monto</TableCell>
                            <TableCell style={{ fontWeight: 600 }}>Juzgado</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!abogadoFiltro ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Typography variant="body2" color="text.secondary" style={{ padding: 20 }}>
                                        Seleccione un abogado del listado para ver sus causas asignadas
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : registrosFiltrados.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Typography variant="body2" color="text.secondary" style={{ padding: 20 }}>
                                        No hay registros con el abogado seleccionado
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            registrosFiltrados.map((registro, idx) => (
                                <TableRow
                                    key={registro.Id}
                                    hover
                                    selected={isSelected(registro.Id)}
                                    onClick={() => handleSelectOne(registro.Id)}
                                    style={{ cursor: 'pointer' }}
                                    sx={{ backgroundColor: idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}
                                >
                                    <TableCell padding="checkbox">
                                        <Checkbox checked={isSelected(registro.Id)} />
                                    </TableCell>
                                    <TableCell>{registro.Fila}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" noWrap style={{ maxWidth: 200 }}>
                                            {registro.Autos || `Exp. ${registro.IdExpediente}`}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" noWrap style={{ maxWidth: 180 }}>
                                            {registro.Campo1}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" noWrap style={{ maxWidth: 220 }}>
                                            {registro.Campo3 || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        {registro.Campo4 ? `$${Number(registro.Campo4).toLocaleString('es-AR', { minimumFractionDigits: 2 })}` : '-'}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" noWrap style={{ maxWidth: 150 }}>
                                            {registro.JuzgadoRadicacion || '-'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Barra de acciones */}
            <Box className={classes.actionsBar}>
                <Box>
                    <Typography variant="body2" color="text.secondary">
                        {seleccionados.length} causa(s) seleccionada(s)
                        {seleccionados.length > 0 && (
                            <Chip
                                label={`${seleccionados.length}`}
                                size="small"
                                color="primary"
                                className={classes.selectedChip}
                            />
                        )}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RemoveCircleIcon />}
                    onClick={handleDesapoderar}
                    disabled={loading || seleccionados.length === 0 || !abogadoFiltro}
                >
                    Desapoderar Abogado Seleccionado
                </Button>
            </Box>

            <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(prev => ({...prev, open: false}))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert elevation={6} variant="filled" severity={snack.severity} onClose={() => setSnack(prev => ({...prev, open: false}))}>
                    {snack.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default DesapoderamientoPanel;
